// Maps between the IDP catalog-structure wire shapes (src/api/access.types.ts)
// and the roles-feature domain types — the single boundary where the wire
// shape is known; the editor and its matrix speak only domain types.
import type {
  CreateCatalogRoleRequestBody,
  DelegationScopeRefResponse,
  DomainStructureResponse,
  ProductStructureResponse,
  ResourceStructureResponse,
  TierStructureResponse,
  UpdateCatalogRoleRequestBody,
} from '@/api/access.types'
import type { DelegationScopeRef, DomainDef, ProductStructure, ResourceDef, RoleInput, Tier } from '../types'

function toScopeRef(response: DelegationScopeRefResponse | null): DelegationScopeRef | null {
  return response ? { id: response.id, value: response.value } : null
}

function toResourceDef(response: ResourceStructureResponse): ResourceDef {
  // `names` is absent on IDP builds that predate the names catalog.
  return {
    id: response.id,
    verbs: response.verbs,
    readScope: toScopeRef(response.readScope),
    writeScope: toScopeRef(response.writeScope),
    names: response.names ?? {},
  }
}

function toDomainDef(response: DomainStructureResponse): DomainDef {
  // `names` is absent on IDP builds that predate the names catalog.
  return { domain: response.domain, resources: response.resources.map(toResourceDef), names: response.names ?? {} }
}

function toTier(response: TierStructureResponse): Tier {
  return { id: response.id, scopes: response.scopes, permissions: response.permissions }
}

export function toProductStructure(response: ProductStructureResponse): ProductStructure {
  return {
    product: response.product,
    domains: response.domains.map(toDomainDef),
    tiers: response.tiers.map(toTier),
  }
}

export function toCreateRequest(product: string, input: RoleInput): CreateCatalogRoleRequestBody {
  return {
    product,
    id: input.id,
    description: input.description,
    scopes: input.scopes,
    permissions: input.permissions,
  }
}

export function toUpdateRequest(input: RoleInput): UpdateCatalogRoleRequestBody {
  return { description: input.description, scopes: input.scopes, permissions: input.permissions }
}
